import { Flex, Text, Divider } from "@chakra-ui/react";
import { InputCurrency } from "@/components";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useEffect, useRef, useState } from "react";
import { useTransfers } from "@/hooks/useTransfer";
import { formatCurrency, formatCurrencyToApi, showToast, stripCurrency } from "@/utils";
import { useRedeem } from "@/hooks";
import { InfoIconNotFilled } from "@/styles/icons/InfoIconNotFilled";
import { Checkbox, Drawer, Pills, Tooltip } from "barte-design-system";

const validationSchema = Yup.object().shape({
    amount: Yup.string(),
});

interface transfersProps {
    isOpen: boolean;
    onClose: () => void;
    data?: any;
}

type FormValues = {
    amount: string;
    useAnticipationAmount: boolean;
};

export default function NewTransfer({ isOpen, onClose, data }: transfersProps) {
    const { requestTransfer, requestAnticipationTransfer } = useTransfers();
    const { getSellerAnticipationAmount, getSellerFraud } = useRedeem();
    const [anticipationValue, setAnticipationValue] = useState({} as any)
    const [useAnticipationAmount, setUseAnticipationAmount] = useState(false);
    const [isFraud, setIsFraud] = useState(false);
    const [isDisabledByHour, setIsDisabledByHour] = useState(false);
    const user = JSON.parse(localStorage.getItem("barte-user-info"));
    const featureToggle = JSON.parse(localStorage.getItem("barte-features-list-user"));
    const [isLoading, setIsLoading] = useState(false);
    const inputWrapperRef = useRef<HTMLDivElement>(null);
    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: yupResolver(validationSchema),
    });
    const formatNumber = (currency) => {
        return parseFloat(currency.replaceAll(/[^0-9,-]/g, '').replaceAll(',', '.'));
    }
    const value = watch("amount");

    async function newTransfer(formData: FormValues) {
        setIsLoading(true);
        try {
            if (useAnticipationAmount) {
                await requestAnticipationTransfer();
                await new Promise(resolve => setTimeout(resolve, 1000));
                await requestTransfer({ amount: anticipationValue?.amountToReceive });
            } else {
                await requestTransfer({ amount: formatNumber(formData?.amount) ?? 0 });
            }
            onClose();
            setIsLoading(false);
            showToast({
                message: "Saque agendado com sucesso!",
                type: "success",
            });

        } catch (error) {
            onClose();
            setIsLoading(false);
            const errorMessage = error?.response?.data?.errors
                ? error.response.data.errors.map(err => `${err.code} - ${err.description}`).join('\n')
                : error.message || "Falha na requisição, por favor tente novamente mais tarde";

            showToast({
                type: "error",
                message: errorMessage
            });

            return error;
        }
    }

    useEffect(() => {
        if (!isOpen) return;
        const formattedValue = formatCurrencyToApi(value);
        if (featureToggle?.includes("view_balance_anticipation") && (user?.roles?.includes("SELLER_ADMIN") || user?.roles?.includes("SELLER_ANTICIPATION"))) {
            getSellerAnticipationAmount({
                requestedAmount: isNaN(formattedValue) ? 0 : formattedValue,
                useAnticipationAmount: useAnticipationAmount
            })
                .then(res => setAnticipationValue(res.data))
                .catch(() => { })
        }
        if (user?.roles?.includes("SELLER_ADMIN")) {
            getSellerFraud(user?.idSeller)
                .then(res => setIsFraud(res?.data?.isFraud))
                .catch(() => { })
        }
    }, [value, useAnticipationAmount, isOpen])

    useEffect(() => {
        if (!isOpen) {
            setValue("amount", "");
            setUseAnticipationAmount(false);
            setAnticipationValue({});
            return;
        }
        const timer = setTimeout(() => {
            inputWrapperRef.current?.querySelector<HTMLInputElement>('input[name="amount"]')?.focus();
        }, 150);
        return () => clearTimeout(timer);
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) return;
        const timeValue = new Date().getUTCHours() - 3;
        if (timeValue >= 23 || timeValue < 1) {
            setIsDisabledByHour(true);
        }
        if (data?.notFormattedAmount < 0) {
            setValue("amount", data?.amount);
            return
        }
    }, [data, isOpen])

    const isSubmitDisabled =
        (formatCurrencyToApi(value) <= 9 && !useAnticipationAmount) ||
        formatCurrencyToApi(value) > data?.notFormattedAmount ||
        isDisabledByHour ||
        isLoading;

    const showAnticipation =
        data?.bankingType !== "BARTE_BANKING" &&
        (user?.roles?.includes("SELLER_ADMIN") || user?.roles?.includes("SELLER_ANTICIPATION")) &&
        featureToggle?.includes("view_balance_anticipation");

    const totalLabel = `R$ ${stripCurrency(data?.totalAmount)}`;

    return (
        <Drawer
            isOpen={isOpen}
            title="Solicitar saque"
            onClose={onClose}
            footer={{
                primaryButton: {
                    children: isLoading ? "Processando..." : "Solicitar saque",
                    onClick: handleSubmit(newTransfer),
                    loading: isLoading,
                    disabled: isSubmitDisabled,
                },
                secondaryButton: {
                    children: "Cancelar",
                    onClick: onClose,
                    variant: "outline",
                },
            }}
        >
            <Flex flexDirection="column" gap="16px" color="var(--content-primary)">
                <Text fontSize="sm" color="var(--content-secondary)">
                    Valores que você pode sacar tanto de saldo atual como antecipar recebíveis futuros.
                </Text>

                {isDisabledByHour && (
                    <Text
                        fontSize=".8rem"
                        textAlign="center"
                        backgroundColor="yellow.100"
                        borderRadius=".5rem"
                        p=".5rem"
                    >
                        A solitação de repasse fica bloqueada no período das 23:00h e 01:00h.
                    </Text>
                )}

                <Flex
                    flexDirection="column"
                    border="1px solid var(--stroke-primary)"
                    borderRadius="8px"
                    px="16px"
                >
                    <Flex justify="space-between" align="center" py="16px">
                        <Text fontSize="sm">Disponível para saque</Text>
                        <Pills label={data?.amount} variant="light" state="success" size="sm" />
                    </Flex>
                    {showAnticipation && (
                        <>
                            <Divider borderColor="var(--stroke-primary)" />
                            <Flex justify="space-between" align="center" py="16px">
                                <Text fontSize="sm">A liberar</Text>
                                <Pills label={data?.provisionedAmount} variant="light" state="warning" size="sm" />
                            </Flex>
                            <Divider borderColor="var(--stroke-primary)" />
                            <Flex justify="space-between" align="center" py="16px">
                                <Text fontSize="sm" fontWeight="600">Total disponível</Text>
                                <Pills label={totalLabel} variant="light" state="default" size="sm" />
                            </Flex>
                        </>
                    )}
                </Flex>

                <Flex
                    ref={inputWrapperRef}
                    flexDirection="column"
                    gap="12px"
                    border="1px solid var(--stroke-primary)"
                    borderRadius="8px"
                    p="16px"
                >
                    <Text fontSize="sm" fontWeight="600">Qual o valor você quer solicitar?</Text>
                    <InputCurrency
                        setValue={setValue}
                        name="amount"
                        register={{ ...register("amount") }}
                        placeholder="Digite o valor"
                        aria-label={"Valor"}
                        aria-invalid={errors.amount ? "true" : "false"}
                        fontSize="1rem"
                        isDisabled={data?.notFormattedAmount === 0 || data?.notFormattedAmount < 0}
                        errors={errors?.amount}
                    />
                    {showAnticipation && (
                        <Checkbox
                            size="sm"
                            label="Incluir saldo a liberar"
                            defaultChecked={useAnticipationAmount}
                            onChange={(event) => setUseAnticipationAmount(event)}
                            disabled={formatCurrencyToApi(data?.provisionedAmount) === 0 || !user?.roles?.includes("SELLER_ANTICIPATION")}
                        />
                    )}
                </Flex>

                {showAnticipation && anticipationValue && (
                    <Flex
                        flexDirection="column"
                        gap="12px"
                        border="1px solid var(--stroke-primary)"
                        borderRadius="8px"
                        p="16px"
                    >
                        <Text fontSize="sm" fontWeight="600">Dados para revisar</Text>
                        <Flex justifyContent="space-between">
                            <Flex align="center" gap="8px">
                                <Tooltip label="Soma do Valor solicitado com o Saldo a liberar, se selecionado">
                                    <Flex align="center" gap="4px">
                                        <Text fontSize=".8rem">Total solicitado</Text>
                                        <InfoIconNotFilled size={10} />
                                    </Flex>
                                </Tooltip>
                            </Flex>
                            <Text fontSize=".8rem">
                                {formatCurrency({ value: anticipationValue?.totalRedeem })}
                            </Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                            <Text fontSize=".8rem">Taxa de antecipação</Text>
                            <Text fontSize=".8rem">
                                {isFraud && useAnticipationAmount ? formatCurrency({ value: anticipationValue?.totalRedeem * 0.10 }) : formatCurrency({ value: anticipationValue?.anticipationTax })}
                            </Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                            <Text as="strong" fontSize=".8rem">Você recebe</Text>
                            <Text as="strong" fontSize=".8rem">
                                {isFraud && useAnticipationAmount ? formatCurrency({ value: anticipationValue?.totalRedeem - (anticipationValue?.totalRedeem * 0.10) }) : formatCurrency({ value: anticipationValue?.amountToReceive })}
                            </Text>
                        </Flex>
                    </Flex>
                )}
            </Flex>
        </Drawer>
    )
}
